import api from '@/plugins/axios';
export { sanitizeContent } from '@/utils/sanitizeHelper';

/**
 * Upload image to backend and return a URL to insert into Quill.
 *
 * Editor preview should be reliable → always insert the raw `/media/...` URL.
 * Saved/Viewer HTML is optimized later by sanitizeContent() and view‑time enhancers
 * (Home/Modal) which convert to `/api/images/serve/?src=/media/...&w=...&f=...`.
 */
export async function uploadImage(file) {
  const form = new FormData();
  form.append('file', file);

  let resp;
  try {
    resp = await api.post(
      '/api/uploads/images/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
    );
  } catch (err) {
    console.error('Image upload failed', err);
    const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
    throw new Error(msg);
  }

  const data    = resp?.data || {};
  const fileUrl = data.file ?? data.url ?? data.file_url; // expected like "/media/..."

  // Normalize any absolute /media/... URL to a relative one
  const toRelative = (url) => {
    if (typeof url !== 'string' || !url) return url;
    try {
      const u = new URL(url, window.location.origin);
      if (u.pathname.startsWith('/media/')) {
        return u.pathname + u.search + u.hash;
      }
      return url; // leave external/CDN URLs untouched
    } catch {
      return url; // already relative or not a full URL
    }
  };

  const relFileUrl = toRelative(fileUrl);

  // Insert raw /media/... so the Quill editor preview is instant & robust
  if (typeof relFileUrl === 'string' && relFileUrl.startsWith('/media/')) {
    return relFileUrl;
  }

  // Final fallback (shouldn’t normally happen)
  return relFileUrl || '';
}

export function imageHandler(quill) {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = async () => {
    const file = input.files && input.files[0];
    if (!file) return;

    // Enforce max file size of 3 MB
    const maxSize = 3 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Image upload failed: file size exceeds 3 MB limit.');
      return;
    }

    try {
      let url = await uploadImage(file);

      // Safety patch: if any older code returns /media/resize/, route it through /api/…
      if (typeof url === 'string' && url.startsWith('/media/resize/')) {
        url = url.replace(/^\/media\/resize\//, '/api/media/resize/');
      }

      // Make sure the editor has focus so getSelection() works
      quill.focus();

      // Try to get a valid range, fallback to end of editor
      const sel = quill.getSelection();
      const index = sel ? sel.index : quill.getLength();

      // Insert the image at that index, with a "user" source
      quill.insertEmbed(index, 'image', url, 'user');

      // Move cursor just after the image
      quill.setSelection(index + 1, 0, 'user');
    } catch (err) {
      console.error('Image upload failed', err);
    }
  };
}
